/**
 * Incident Automation Service
 * Dynamic forms, 24-hour countdown, trend analysis, and guided workflows
 */

import LocalStorageService from '../storage/LocalStorageService';

class IncidentAutomationService {
  constructor() {
    this.storageKey = 'incidents';
    this.sirsKey = 'sirs_reports';
    this.trendsKey = 'incident_trends';
  }

  /**
   * Get dynamic form fields based on incident type
   */
  getDynamicFormFields(incidentType, clientType = 'agedcare') {
    const baseFields = [
      { id: 'date', type: 'date', label: 'Incident Date', required: true },
      { id: 'time', type: 'time', label: 'Incident Time', required: true },
      { id: 'location', type: 'text', label: 'Location', required: true },
      { id: 'description', type: 'textarea', label: 'Description', required: true }
    ];

    const typeSpecificFields = {
      'fall': [
        { id: 'injury', type: 'select', label: 'Did injury occur?', options: ['Yes', 'No'], required: true },
        { id: 'medicalTreatment', type: 'select', label: 'Medical treatment required?', options: ['Yes', 'No'], required: true },
        { id: 'fracture', type: 'checkbox', label: 'Fracture suspected' }
      ],
      'aggression': [
        { id: 'aggressor', type: 'text', label: 'Who was aggressive?', required: true },
        { id: 'target', type: 'text', label: 'Who was targeted?', required: true },
        { id: 'weapon', type: 'checkbox', label: 'Weapon involved' },
        { id: 'injury', type: 'select', label: 'Did injury occur?', options: ['Yes', 'No'], required: true }
      ],
      'medication_error': [
        { id: 'medication', type: 'text', label: 'Medication involved', required: true },
        { id: 'errorType', type: 'select', label: 'Error Type', options: ['Wrong medication', 'Wrong dose', 'Wrong time', 'Wrong route', 'Omitted'], required: true },
        { id: 'harm', type: 'select', label: 'Harm caused?', options: ['None', 'Minor', 'Moderate', 'Severe'], required: true }
      ],
      'physical_restraint': [
        { id: 'restraintType', type: 'select', label: 'Restraint Type', options: ['Physical', 'Chemical', 'Mechanical', 'Environmental'], required: true },
        { id: 'authorised', type: 'select', label: 'Authorised?', options: ['Yes', 'No'], required: true },
        { id: 'authorisationEvidence', type: 'file', label: 'Authorisation Evidence', required: true },
        { id: 'duration', type: 'number', label: 'Duration (minutes)', required: true }
      ],
      'chemical_restraint': [
        { id: 'medication', type: 'text', label: 'Medication used', required: true },
        { id: 'authorised', type: 'select', label: 'Authorised?', options: ['Yes', 'No'], required: true },
        { id: 'authorisationEvidence', type: 'file', label: 'Authorisation Evidence', required: true },
        { id: 'behaviorSupportPlan', type: 'text', label: 'Behavior Support Plan ID', required: true }
      ]
    };

    const fields = [...baseFields];
    
    if (typeSpecificFields[incidentType]) {
      fields.push(...typeSpecificFields[incidentType]);
    }

    // Add SIRS fields for Aged Care
    if (clientType === 'agedcare') {
      fields.push({
        id: 'sirsPriority',
        type: 'select',
        label: 'SIRS Priority',
        options: ['Priority 1', 'Priority 2'],
        required: true,
        conditional: true // Only show if injury/medical treatment = Yes
      });
    }

    return fields;
  }

  /**
   * Classify incident priority
   */
  classifyPriority(incidentData, clientType = 'agedcare') {
    let priority = 'Priority 2';
    
    // Check for Priority 1 indicators
    if (incidentData.injury === 'Yes' || incidentData.medicalTreatment === 'Yes') {
      priority = 'Priority 1';
    }
    
    if (incidentData.harm && ['Moderate', 'Severe'].includes(incidentData.harm)) {
      priority = 'Priority 1';
    }
    
    if (incidentData.unauthorised === 'Yes' && incidentData.restraintType) {
      priority = 'Priority 1';
    }
    
    return {
      priority,
      reportingDeadline: this.calculateReportingDeadline(priority),
      countdownHours: this.calculateCountdownHours(priority)
    };
  }

  /**
   * Calculate reporting deadline
   */
  calculateReportingDeadline(priority) {
    const deadline = new Date();
    if (priority === 'Priority 1') {
      deadline.setHours(deadline.getHours() + 24); // 24 hours
    } else {
      deadline.setDate(deadline.getDate() + 30); // 30 days
    }
    return deadline.toISOString();
  }

  /**
   * Calculate countdown hours
   */
  calculateCountdownHours(priority) {
    if (priority === 'Priority 1') {
      return 24;
    }
    return 720; // 30 days in hours
  }

  /**
   * Create incident with automation
   */
  async createIncident(incidentData) {
    try {
      const classification = this.classifyPriority(incidentData, incidentData.clientType);
      
      const incident = {
        id: `INC-${Date.now()}`,
        ...incidentData,
        priority: classification.priority,
        reportingDeadline: classification.reportingDeadline,
        countdownHours: classification.countdownHours,
        status: 'open',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      const created = LocalStorageService.addItem(this.storageKey, incident);
      
      // Auto-trigger SIRS if Priority 1 and Aged Care
      if (classification.priority === 'Priority 1' && incidentData.clientType === 'agedcare') {
        await this.triggerSIRSWorkflow(created.id);
      }
      
      // Auto-schedule reporting deadline
      await this.scheduleReportingDeadline(created.id, classification.reportingDeadline);
      
      // Check for systemic risks
      await this.checkSystemicRisks(incidentData.participantId || incidentData.residentId);
      
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Trigger SIRS workflow
   */
  async triggerSIRSWorkflow(incidentId) {
    try {
      const sirsReport = {
        id: `SIRS-${Date.now()}`,
        incidentId,
        status: 'pending',
        priority: 'Priority 1',
        createdAt: new Date().toISOString(),
        dueDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24 hours
      };
      
      LocalStorageService.addItem(this.sirsKey, sirsReport);
      
      return { success: true, data: sirsReport };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Schedule reporting deadline in compliance calendar
   */
  async scheduleReportingDeadline(incidentId, deadline) {
    try {
      // In production, this would integrate with ComplianceCalendar service
      const calendarEvent = {
        id: `CAL-${Date.now()}`,
        type: 'incident_reporting',
        incidentId,
        deadline,
        status: 'pending',
        createdAt: new Date().toISOString()
      };
      
      LocalStorageService.addItem('compliance_calendar', calendarEvent);
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Check for systemic risks
   */
  async checkSystemicRisks(participantId) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - 14); // Last 2 weeks
      
      const recentIncidents = LocalStorageService.get(this.storageKey, [])
        .filter(i => 
          (i.participantId === participantId || i.residentId === participantId) &&
          new Date(i.createdAt) >= cutoffDate &&
          i.type === 'fall' // Example: check for falls
        );
      
      if (recentIncidents.length >= 3) {
        // Systemic risk detected
        const riskAlert = {
          id: `RISK-${Date.now()}`,
          type: 'systemic_risk',
          participantId,
          incidentType: 'fall',
          count: recentIncidents.length,
          period: '14 days',
          message: `${recentIncidents.length} falls in 2 weeks at same time of day. Systemic risk detected.`,
          recommendation: 'Review care plan, medication timing, or environmental factors.',
          createdAt: new Date().toISOString()
        };
        
        LocalStorageService.addItem('risk_alerts', riskAlert);
        
        return {
          success: true,
          systemicRisk: true,
          alert: riskAlert
        };
      }
      
      return { success: true, systemicRisk: false };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get incident trends (heatmap data)
   */
  async getIncidentTrends(facilityId, months = 6) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setMonth(cutoffDate.getMonth() - months);
      
      const incidents = LocalStorageService.get(this.storageKey, [])
        .filter(i => 
          i.facilityId === facilityId &&
          new Date(i.createdAt) >= cutoffDate
        );
      
      // Group by type and date
      const trends = {};
      incidents.forEach(incident => {
        const date = new Date(incident.createdAt).toISOString().split('T')[0];
        const type = incident.type || 'other';
        
        if (!trends[type]) {
          trends[type] = {};
        }
        
        if (!trends[type][date]) {
          trends[type][date] = 0;
        }
        
        trends[type][date]++;
      });
      
      // Calculate heatmap intensity
      const maxCount = Math.max(...Object.values(trends).flatMap(t => Object.values(t)), 0);
      
      return {
        success: true,
        data: {
          trends,
          maxCount,
          totalIncidents: incidents.length,
          period: `${months} months`
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get countdown status for incident
   */
  async getCountdownStatus(incidentId) {
    try {
      const incident = LocalStorageService.findById(this.storageKey, incidentId);
      
      if (!incident) {
        return { success: false, error: 'Incident not found' };
      }
      
      const now = new Date();
      const deadline = new Date(incident.reportingDeadline);
      const hoursRemaining = (deadline - now) / (1000 * 60 * 60);
      
      let status = 'ok';
      if (hoursRemaining < 0) {
        status = 'overdue';
      } else if (hoursRemaining < 4) {
        status = 'critical';
      } else if (hoursRemaining < 12) {
        status = 'urgent';
      }
      
      return {
        success: true,
        data: {
          hoursRemaining: Math.max(0, hoursRemaining),
          status,
          deadline: incident.reportingDeadline,
          priority: incident.priority
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get pending SIRS reports
   */
  async getPendingSIRSReports(facilityId = null) {
    try {
      const all = LocalStorageService.getAll(this.storageKey);
      let filtered = all.filter(i => 
        i.priority === 'Priority 1' || i.priority === 'Priority 2'
      );
      
      if (facilityId) {
        filtered = filtered.filter(i => i.facilityId === facilityId);
      }
      
      // Get countdown status for each
      const pending = await Promise.all(
        filtered.map(async (incident) => {
          const countdown = await this.getCountdownStatus(incident.id);
          return {
            ...incident,
            hoursRemaining: countdown.success ? countdown.data.hoursRemaining : 0,
            countdownStatus: countdown.success ? countdown.data.status : 'unknown'
          };
        })
      );
      
      return { success: true, data: pending };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

export default new IncidentAutomationService();

